import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import {
  Users, Plus, Download, Search, Filter, ChevronLeft, ChevronRight,
  Building2, User as UserIcon, ShieldCheck, Phone, Mail, MapPin, Merge, Trash2, X,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ClientsExportDialog from "@/components/ClientsExportDialog";
import ClientsFusionDialog from "@/components/ClientsFusionDialog";
import ClientsSuppressionDialog from "@/components/ClientsSuppressionDialog";

const STATUT_BADGE = {
  actif: "bg-green-50 text-green-700 border-green-200",
  inactif: "bg-gray-100 text-gray-700 border-gray-200",
  bloque: "bg-red-50 text-red-700 border-red-200",
};

const ABO_BADGE = {
  S: "bg-blue-50 text-blue-700 border-blue-200",
  M: "bg-purple-50 text-purple-700 border-purple-200",
  L: "bg-amber-50 text-amber-700 border-amber-200",
  flotte: "bg-[#52a675]/10 text-[#3d8a5c] border-[#52a675]/30",
  aucun: "bg-gray-50 text-gray-500 border-gray-200",
};

export default function ClientsList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState({ items: [], total: 0, page: 1 });
  const [q, setQ] = useState("");
  const [statut, setStatut] = useState("");
  const [type, setType] = useState("");
  const [abonnement, setAbonnement] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);   // iter240
  const [selection, setSelection] = useState([]);        // iter314 — ids cochés
  const [fusionOpen, setFusionOpen] = useState(false);
  const [suppressionOpen, setSuppressionOpen] = useState(false);
  const limit = 25;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (q) params.q = q;
      if (statut) params.statut = statut;
      if (type) params.type = type;
      if (abonnement) params.abonnement = abonnement;
      const { data: resp } = await api.get("/clients", { params });
      setData(resp);
    } finally {
      setLoading(false);
    }
  }, [q, statut, type, abonnement, page]);

  useEffect(() => {
    const t = setTimeout(fetchData, 250);
    return () => clearTimeout(t);
  }, [fetchData]);

  // La sélection ne survit pas à un changement de page ou de filtre : on ne coche
  // que ce qu'on voit, pour ne jamais agir sur une fiche hors de l'écran.
  useEffect(() => { setSelection([]); }, [page, q, statut, type, abonnement]);

  const totalPages = Math.max(1, Math.ceil(data.total / limit));
  const idsPage = useMemo(() => data.items.map((c) => c.id), [data.items]);
  const toutCoche = idsPage.length > 0 && selection.length === idsPage.length;
  const basculer = (id) =>
    setSelection((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const rafraichir = () => { setSelection([]); fetchData(); };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="clients-list-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-[#52a675]" /> Clients
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {data.total} client{data.total > 1 ? "s" : ""} au total
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setExportOpen(true)} data-testid="export-csv-button">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button
            onClick={() => navigate("/clients/nouveau")}
            className="bg-[#52a675] hover:bg-[#3d8a5c]"
            data-testid="new-client-button"
          >
            <Plus className="w-4 h-4 mr-2" /> Nouveau client
          </Button>
        </div>
      </div>

      <Card className="p-4 mb-4 border-gray-200">
        <div className="grid md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Nom, prénom, raison sociale, tel, email, plaque, marque, modèle, n° Bexio…"
              className="pl-10"
              data-testid="filter-search"
            />
          </div>
          <Select value={type || "all"} onValueChange={(v) => { setType(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous types</SelectItem>
              <SelectItem value="particulier">Particulier</SelectItem>
              <SelectItem value="entreprise">Entreprise</SelectItem>
              <SelectItem value="assurance">Assurance</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statut || "all"} onValueChange={(v) => { setStatut(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger data-testid="filter-statut"><SelectValue placeholder="Statut" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              <SelectItem value="actif">Actif</SelectItem>
              <SelectItem value="inactif">Inactif</SelectItem>
              <SelectItem value="bloque">Bloqué</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="border-gray-200 overflow-hidden">
        {selection.length > 0 && (
          <div
            className="flex items-center gap-2 flex-wrap px-4 py-3 bg-[#f0f9f4] border-b border-[#52a675]/30"
            data-testid="clients-barre-selection"
          >
            <span className="text-sm font-semibold text-[#3d8a5c]" data-testid="clients-selection-count">
              {selection.length} sélectionné{selection.length > 1 ? "s" : ""}
            </span>
            <Button variant="outline" size="sm" onClick={() => setSelection([])} data-testid="selection-vider">
              <X className="w-3.5 h-3.5 mr-1" /> Désélectionner
            </Button>
            <div className="flex-1" />
            <Button variant="outline" size="sm" onClick={() => setExportOpen(true)} data-testid="selection-export">
              <Download className="w-3.5 h-3.5 mr-1" /> Télécharger les fiches
            </Button>
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                disabled={selection.length < 2}
                title={selection.length < 2 ? "Cochez au moins deux fiches à fusionner" : undefined}
                onClick={() => setFusionOpen(true)}
                data-testid="selection-fusion"
              >
                <Merge className="w-3.5 h-3.5 mr-1" /> Fusionner
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                onClick={() => setSuppressionOpen(true)}
                data-testid="selection-supprimer"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer
              </Button>
            )}
          </div>
        )}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead className="w-10">
                  <input
                    type="checkbox"
                    className="accent-[#52a675] cursor-pointer"
                    checked={toutCoche}
                    onChange={() => setSelection(toutCoche ? [] : idsPage)}
                    aria-label="Tout sélectionner"
                    data-testid="clients-select-all"
                  />
                </TableHead>
                <TableHead className="w-12"></TableHead>
                <TableHead>Nom / Raison sociale</TableHead>
                <TableHead>Localité</TableHead>
                <TableHead>Téléphone</TableHead>
                <TableHead>Abonnement</TableHead>
                <TableHead>Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-gray-400">Chargement…</TableCell></TableRow>
              )}
              {!loading && data.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-gray-400">
                    Aucun client trouvé
                  </TableCell>
                </TableRow>
              )}
              {!loading && data.items.map((c) => {
                const name = c.raison_sociale || `${c.prenom || ""} ${c.nom || ""}`.trim();
                const Icon = c.type === "assurance" ? ShieldCheck : (c.type === "entreprise" ? Building2 : UserIcon);
                const typeLabel = c.type === "assurance" ? "Assurance" : (c.type === "entreprise" ? "Entreprise" : "Particulier");
                const iconBg = c.type === "assurance" ? "bg-violet-50 text-violet-600" : (c.type === "entreprise" ? "bg-amber-50 text-amber-600" : "bg-blue-50 text-blue-600");
                return (
                  <TableRow
                    key={c.id}
                    onClick={() => navigate(`/clients/${c.id}`)}
                    className={`cursor-pointer hover:bg-[#f0f9f4] ${selection.includes(c.id) ? "bg-[#f0f9f4]" : ""}`}
                    data-testid={`client-row-${c.id}`}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="accent-[#52a675] cursor-pointer"
                        checked={selection.includes(c.id)}
                        onChange={() => basculer(c.id)}
                        aria-label={`Sélectionner ${name}`}
                        data-testid={`client-check-${c.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className={`w-9 h-9 rounded-full ${iconBg} flex items-center justify-center`}>
                        <Icon className="w-4 h-4" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-semibold text-gray-900">{name}</div>
                      <div className="text-xs text-gray-500">{typeLabel} {c.num_bexio && ` · #${c.num_bexio}`}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm flex items-center gap-1 text-gray-700">
                        <MapPin className="w-3.5 h-3.5 text-gray-400" />
                        {c.npa} {c.localite}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm flex items-center gap-1 text-gray-700">
                        <Phone className="w-3.5 h-3.5 text-gray-400" />
                        {c.tel_principal}
                      </div>
                      {c.email && (
                        <div className="text-xs flex items-center gap-1 text-gray-500 mt-0.5">
                          <Mail className="w-3 h-3" />
                          {c.email}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={ABO_BADGE[c.abonnement || "aucun"]}>
                        {c.abonnement && c.abonnement !== "aucun" ? `Abonnement ${c.abonnement.toUpperCase()}` : "Sans abo"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUT_BADGE[c.statut] || STATUT_BADGE.actif}>
                        {c.statut}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {totalPages > 1 && (
          <div className="p-4 border-t border-gray-100 flex items-center justify-between">
            <div className="text-xs text-gray-500">Page {data.page} sur {totalPages}</div>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                data-testid="pagination-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
                data-testid="pagination-next"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      <ClientsExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        filtres={
          selection.length > 0
            ? { ids: selection.join(",") }
            : { q: q || undefined, type: type || undefined, statut: statut || undefined, abonnement: abonnement || undefined }
        }
        total={selection.length > 0 ? selection.length : data.total}
        limiteSelection={selection.length > 0}
      />

      <ClientsFusionDialog
        open={fusionOpen}
        onClose={() => setFusionOpen(false)}
        ids={selection}
        onDone={rafraichir}
      />

      <ClientsSuppressionDialog
        open={suppressionOpen}
        onClose={() => setSuppressionOpen(false)}
        ids={selection}
        onDone={rafraichir}
      />
    </div>
  );
}
